import { useState, useEffect, useCallback,} from 'react';
import toast from 'react-hot-toast';
import ReactConfetti from 'react-confetti';

import StarCanvas    from './components/StarCanvas';
import Navbar        from './components/Navbar';
import Card          from './components/Card';
import Sidebar       from './components/Sidebar';
import TrailerModal  from './components/TrailerModal';
import AIChatWidget  from './components/AIChatWidget';
import Footer        from './components/Footer';

import { USERS, MOODS, ITEMS, REVIEWS, LOADING_STEPS } from './data/data';

// ── Skeleton card ────────────────────────────────────────────────────────────
function SkeletonCard() {
  const sh = { background: 'linear-gradient(90deg,var(--surface) 0%,var(--surface2) 50%,var(--surface) 100%)', backgroundSize: '200% 100%', animation: 'shimmer 1.6s ease infinite', borderRadius: 6 };
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 20, overflow: 'hidden' }}>
      <div style={{ ...sh, height: 210, borderRadius: 0 }} />
      <div style={{ padding: '1.15rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        <div style={{ ...sh, height: 20, width: '80%' }} />
        <div style={{ ...sh, height: 12, width: '100%' }} />
        <div style={{ ...sh, height: 12, width: '60%' }} />
        <div style={{ ...sh, height: 12, width: '80%' }} />
        <div style={{ ...sh, height: 38, marginTop: 8 }} />
      </div>
    </div>
  );
}

// ── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [theme,      setTheme]      = useState('dark');
  const [user,       setUser]       = useState(USERS[0]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [mood,       setMood]       = useState('all');
  const [items,      setItems]      = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [loadStep,   setLoadStep]   = useState(0);
  const [liked,      setLiked]      = useState(new Set());
  const [watchlist,  setWatchlist]  = useState([]);
  const [trailer,    setTrailer]    = useState(null);
  const [confetti,   setConfetti]   = useState(false);
  const [winW,       setWinW]       = useState(window.innerWidth);
  const [winH,       setWinH]       = useState(window.innerHeight);

  // Track window size for confetti
  useEffect(() => {
    const h = () => { setWinW(window.innerWidth); setWinH(window.innerHeight); };
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // Apply theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Load recommendations for a user
  const loadRecs = useCallback((u) => {
    setLoading(true); setItems([]); setLoadStep(0);
    let s = 0;
    const iv = setInterval(() => {
      s++; setLoadStep(s);
      if (s >= LOADING_STEPS.length) {
        clearInterval(iv);
        setTimeout(() => {
          setItems(ITEMS.filter(i => i.users.includes(u.id)));
          setLoading(false);
        }, 350);
      }
    }, 360);
  }, []);

  useEffect(() => { loadRecs(USERS[0]); }, [loadRecs]);

  const selectUser = (u) => {
    setUser(u); setTypeFilter('all'); setMood('all'); setLiked(new Set());
    loadRecs(u);
    toast(`Switched to ${u.name}'s profile`);
  };

  const toggleLike = (id) => {
    setLiked(prev => {
      const s = new Set(prev);
      const adding = !s.has(id);
      adding ? s.add(id) : s.delete(id);
      if (adding) {
        setConfetti(true);
        setTimeout(() => setConfetti(false), 3200);
        toast.success('♥ Liked — refining your AI model');
      } else {
        toast('Removed from liked');
      }
      return s;
    });
  };

  const toggleWatchlist = (item) => {
    const has = watchlist.some(w => w.id === item.id);
    if (has) {
      setWatchlist(p => p.filter(w => w.id !== item.id));
      toast.error('Removed from watchlist');
    } else {
      setWatchlist(p => [...p, item]);
      toast.success(`"${item.title}" added to watchlist ✓`);
    }
  };

  // Filter items
  let filtered = items;
  if (typeFilter !== 'all') filtered = filtered.filter(i => i.type === typeFilter);
  if (mood !== 'all')       filtered = filtered.filter(i => i.mood?.includes(mood));

  const topItem = filtered[0];
  const rest    = filtered.slice(1);

  // ── Shared styles ──────────────────────────────────────────────────────────
  const wrap    = { maxWidth: 1380, margin: '0 auto', padding: '0 2rem' };
  const section = { padding: '3rem 0' };
  const eyebrow = { fontFamily: 'var(--font-mono)', fontSize: '0.63rem', letterSpacing: '0.18em', color: 'var(--teal)', textTransform: 'uppercase', marginBottom: '0.5rem' };
  const heading = { fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 700, marginBottom: '1.75rem' };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'var(--font-body)', position: 'relative', overflowX: 'hidden' }}>
      <StarCanvas />
      {confetti && <ReactConfetti width={winW} height={winH} recycle={false} numberOfPieces={200} colors={['#e8b84b','#00d4b4','#7c6bff','#ff4d7a','#22c55e','#38bdf8']} />}

      {/* Noise grain */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 1, opacity: 0.02, backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }} />

      <div style={{ position: 'relative', zIndex: 2 }}>
        {/* ── NAV ── */}
        <Navbar user={user} wlCount={watchlist.length} theme={theme} onThemeToggle={() => setTheme(t => t === 'dark' ? 'light' : 'dark')} />

        {/* ── HERO ── */}
        <section style={{ minHeight: '90vh', display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden', padding: '5rem 0' }}>
          {/* Background image */}
          <div style={{ position: 'absolute', inset: 0 }}>
            <img src="https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=1600&q=55" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.1, filter: 'saturate(.4)' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, transparent 0%, var(--bg) 72%)' }} />
          </div>

          <div style={{ ...wrap, width: '100%', position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '2rem', flexWrap: 'wrap' }}>
            {/* Left content */}
            <div style={{ maxWidth: 680 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem', background: 'rgba(232,184,75,.08)', border: '1px solid rgba(232,184,75,.22)', borderRadius: 100, padding: '0.4rem 1rem 0.4rem 0.6rem', marginBottom: '2rem', animation: 'fadeUp .7s ease both' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--gold)', animation: 'pulse 2s ease infinite', display: 'inline-block' }} />
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.67rem', color: 'var(--gold)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>AI-Powered · Personalised · Real-time</span>
              </div>

              <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(3rem,7vw,6rem)', fontWeight: 700, lineHeight: 1.02, letterSpacing: '-0.02em', marginBottom: '1.5rem', animation: 'fadeUp .7s .1s ease both' }}>
                <span style={{ fontStyle: 'italic', color: 'var(--gold)' }}>Think</span> smarter,<br />
                <span style={{ color: 'var(--teal)' }}>See</span> what you'll{' '}
                <span style={{ color: 'var(--rose)' }}>love</span>
              </h1>

              <p style={{ fontSize: '1.05rem', color: 'var(--text2)', lineHeight: 1.75, maxWidth: 540, marginBottom: '2.5rem', animation: 'fadeUp .7s .2s ease both' }}>
                Hybrid AI recommendations — collaborative filtering meets content intelligence. Watch trailers right inside the app, save your watchlist, and let AI find your next obsession.
              </p>

              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', animation: 'fadeUp .7s .3s ease both' }}>
                <button
                  onClick={() => { loadRecs(user); toast('Refreshing AI recommendations…'); }}
                  style={{ background: 'linear-gradient(135deg,var(--gold),#c8880a)', color: '#040508', border: 'none', padding: '0.9rem 2.2rem', borderRadius: 100, fontWeight: 800, fontSize: '0.9rem', boxShadow: '0 8px 32px rgba(232,184,75,.35)', cursor: 'pointer', transition: 'all 0.28s' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 16px 48px rgba(232,184,75,.5)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(232,184,75,.35)'; }}
                >✦ Get My Recommendations</button>
                <button
                  onClick={() => { const m = ITEMS.find(i => i.trailerUrl); if (m) setTrailer(m); }}
                  style={{ background: 'transparent', border: '1px solid var(--border2)', color: 'var(--text)', padding: '0.9rem 2.2rem', borderRadius: 100, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', transition: 'all 0.28s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--teal)'; e.currentTarget.style.color = 'var(--teal)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text)'; }}
                >▶ Watch a Trailer</button>
              </div>

              {/* Stats */}
              <div style={{ display: 'flex', gap: '2.5rem', marginTop: '3.5rem', animation: 'fadeUp .7s .4s ease both', flexWrap: 'wrap' }}>
                {[{ n: '50K+', l: 'Titles' }, { n: '98.4%', l: 'Accuracy' }, { n: '2M+', l: 'Users' }, { n: 'Live', l: 'Updates' }].map(({ n, l }) => (
                  <div key={l} style={{ borderLeft: '2px solid var(--border2)', paddingLeft: '1.25rem' }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: '2.2rem', fontWeight: 700, color: 'var(--gold)', lineHeight: 1, display: 'block' }}>{n}</span>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.62rem', color: 'var(--muted)', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: '0.25rem' }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right floating cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: 370, flexShrink: 0, animation: 'fadeUp .8s .5s ease both' }}>
              {ITEMS.slice(0, 4).map(it => (
                <div
                  key={it.id}
                  onClick={() => it.trailerUrl && setTrailer(it)}
                  style={{ background: 'rgba(16,20,31,.9)', backdropFilter: 'blur(20px)', border: '1px solid var(--border2)', borderRadius: 20, padding: '1.05rem', display: 'flex', alignItems: 'center', gap: '0.9rem', cursor: it.trailerUrl ? 'pointer' : 'default', transition: 'all 0.28s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--teal)'; e.currentTarget.style.transform = 'translateX(-6px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.transform = 'translateX(0)'; }}
                >
                  <img src={it.img} alt={it.title} style={{ width: 50, height: 50, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: '0.87rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.title}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.61rem', color: 'var(--muted)', marginTop: 2 }}>{it.year} · {it.by}</div>
                  </div>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', padding: '0.28rem 0.58rem', borderRadius: 6, flexShrink: 0, background: it.score >= 90 ? 'rgba(34,197,94,.12)' : 'rgba(245,158,11,.1)', color: it.score >= 90 ? 'var(--green)' : 'var(--amber)', border: `1px solid ${it.score >= 90 ? 'rgba(34,197,94,.25)' : 'rgba(245,158,11,.22)'}` }}>★{it.score}%</span>
                  {it.trailerUrl && <span style={{ flexShrink: 0, fontSize: '0.75rem', color: 'var(--rose)' }}>▶</span>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── TICKER ── */}
        <div style={{ overflow: 'hidden', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', padding: '0.62rem 0', background: 'var(--bg1)' }}>
          <div style={{ display: 'flex', gap: '2.5rem', animation: 'ticker 40s linear infinite', width: 'max-content' }}>
            {[...ITEMS, ...ITEMS].map((it, i) => (
              <span
                key={i}
                onClick={() => it.trailerUrl && setTrailer(it)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexShrink: 0, cursor: it.trailerUrl ? 'pointer' : 'default', padding: '0.1rem 0.5rem', borderRadius: 6 }}
              >
                <img src={it.img} alt="" style={{ width: 26, height: 26, borderRadius: 6, objectFit: 'cover' }} />
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.69rem', whiteSpace: 'nowrap', color: 'var(--text2)' }}>{it.title}</span>
                <span style={{ fontSize: '0.66rem', color: 'var(--teal)' }}>+{it.score}%</span>
                <span style={{ color: 'var(--dim)' }}>·</span>
              </span>
            ))}
          </div>
        </div>

        <div style={wrap}>

          {/* ── PROFILES ── */}
          <div style={section}>
            <div style={eyebrow}>Active Profiles</div>
            <div style={heading}>Who's <em style={{ fontStyle: 'italic', color: 'var(--gold)' }}>watching?</em></div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: '1rem', marginBottom: '2.5rem' }}>
              {USERS.map(u => (
                <div
                  key={u.id}
                  onClick={() => selectUser(u)}
                  style={{ background: 'var(--surface)', border: `2px solid ${user.id === u.id ? 'var(--teal)' : 'var(--border)'}`, borderRadius: 20, padding: '1.4rem', cursor: 'pointer', transition: 'all 0.28s', position: 'relative', boxShadow: user.id === u.id ? '0 0 40px rgba(0,212,180,.15)' : 'none' }}
                  onMouseEnter={e => { if (user.id !== u.id) e.currentTarget.style.transform = 'translateY(-3px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
                >
                  {user.id === u.id && <div style={{ position: 'absolute', top: '0.9rem', right: '0.9rem', width: 8, height: 8, borderRadius: '50%', background: 'var(--teal)', boxShadow: '0 0 8px var(--teal)', animation: 'pulse 2s ease infinite' }} />}
                  <div style={{ width: 50, height: 50, borderRadius: 14, background: u.bg, color: u.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', fontWeight: 900, marginBottom: '1rem' }}>{u.av}</div>
                  <div style={{ fontWeight: 800, fontSize: '0.95rem', marginBottom: '0.2rem', color: user.id === u.id ? 'var(--teal)' : 'var(--text)' }}>{u.name}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.61rem', color: 'var(--muted)', lineHeight: 1.6 }}>{u.genres.join(' · ')}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.61rem', color: 'var(--muted)', marginTop: 3 }}>🎭 {u.mood}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── RECOMMENDATIONS ── */}
          <div style={section}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '1.75rem', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <div style={eyebrow}>AI Picks for {user.name}</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 700 }}>Your <em style={{ fontStyle: 'italic', color: 'var(--gold)' }}>Picks</em></div>
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--muted)' }}>
                {loading ? 'Computing…' : `${filtered.length} recommendations`}
              </div>
            </div>

            {/* Type filter */}
            <div style={{ display: 'flex', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 13, padding: '0.3rem', gap: '0.2rem', width: 'fit-content', marginBottom: '1rem' }}>
              {['all', 'movie', 'book', 'product'].map(f => (
                <button
                  key={f}
                  onClick={() => setTypeFilter(f)}
                  style={{ padding: '0.45rem 1.1rem', borderRadius: 9, border: 'none', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', transition: 'all 0.25s', background: typeFilter === f ? 'linear-gradient(135deg,var(--teal),var(--teal))' : 'transparent', color: typeFilter === f ? '#040508' : 'var(--muted)', boxShadow: typeFilter === f ? '0 4px 14px rgba(0,212,180,.3)' : 'none' }}
                >
                  {f === 'all' ? 'All' : f === 'movie' ? '◉ Films' : f === 'book' ? '◎ Books' : '◈ Products'}
                </button>
              ))}
            </div>

            {/* Mood filter */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
              {MOODS.map(m => (
                <div
                  key={m.id}
                  onClick={() => setMood(m.id)}
                  style={{ background: mood === m.id ? 'rgba(232,184,75,.08)' : 'var(--surface)', border: `1px solid ${mood === m.id ? 'var(--gold)' : 'var(--border)'}`, borderRadius: 100, padding: '0.4rem 1rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.25s', color: mood === m.id ? 'var(--gold)' : 'var(--text)', display: 'flex', alignItems: 'center', gap: '0.35rem', boxShadow: mood === m.id ? '0 0 18px rgba(232,184,75,.12)' : 'none' }}
                >
                  {m.e} {m.l}
                </div>
              ))}
            </div>

            {/* Grid + Sidebar */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '2rem', alignItems: 'start' }}>
              <div>
                {loading ? (
                  <>
                    <div style={{ textAlign: 'center', padding: '2rem 0 1.5rem', fontFamily: 'var(--font-mono)', fontSize: '0.74rem', color: 'var(--teal)' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
                        <div style={{ width: 40, height: 40, border: '2px solid var(--border2)', borderTopColor: 'var(--teal)', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
                        <div style={{ width: 26, height: 26, border: '2px solid var(--border2)', borderBottomColor: 'var(--violet)', borderRadius: '50%', animation: 'spin .5s linear infinite reverse' }} />
                      </div>
                      {LOADING_STEPS[Math.min(loadStep, LOADING_STEPS.length - 1)]}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: '1.25rem' }}>
                      {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
                    </div>
                  </>
                ) : filtered.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '4rem', fontFamily: 'var(--font-mono)', fontSize: '0.78rem', color: 'var(--muted)' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>∅</div>
                    No recommendations for this filter combination.
                  </div>
                ) : (
                  <>
                    {topItem && (
                      <div style={{ marginBottom: '1.25rem' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                          <Card item={topItem} featured liked={liked.has(topItem.id)} watchlisted={watchlist.some(w => w.id === topItem.id)} onLike={toggleLike} onWatchlist={toggleWatchlist} onTrailer={setTrailer} delay={0} />
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: '1.25rem' }}>
                      {rest.map((item, i) => (
                        <Card key={item.id} item={item} liked={liked.has(item.id)} watchlisted={watchlist.some(w => w.id === item.id)} onLike={toggleLike} onWatchlist={toggleWatchlist} onTrailer={setTrailer} delay={(i + 1) * 65} />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {!loading && (
                <Sidebar
                  user={user}
                  items={filtered}
                  watchlist={watchlist}
                  onRemove={id => { setWatchlist(p => p.filter(w => w.id !== id)); toast.error('Removed from watchlist'); }}
                />
              )}
            </div>
          </div>

          {/* ── REVIEWS ── */}
          <div style={section}>
            <div style={eyebrow}>Community</div>
            <div style={heading}>What people <em style={{ fontStyle: 'italic', color: 'var(--gold)' }}>say</em></div>
            <div style={{ display: 'flex', gap: '1rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
              {REVIEWS.map(r => (
                <div
                  key={r.id}
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 20, padding: '1.25rem', minWidth: 300, flexShrink: 0, transition: 'all 0.25s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.85rem' }}>
                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: r.bg, color: r.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.9rem', flexShrink: 0 }}>{r.av}</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{r.name}</div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.59rem', color: 'var(--muted)' }}>{r.date}</div>
                    </div>
                  </div>
                  <div style={{ color: 'var(--gold)', fontSize: '0.84rem', marginBottom: '0.5rem' }}>{'★'.repeat(r.stars)}{'☆'.repeat(5 - r.stars)}</div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text2)', lineHeight: 1.65, fontStyle: 'italic' }}>{r.text}</div>
                  <div style={{ display: 'inline-flex', background: 'rgba(0,212,180,.07)', border: '1px solid rgba(0,212,180,.15)', color: 'var(--teal)', fontFamily: 'var(--font-mono)', fontSize: '0.59rem', padding: '0.18rem 0.5rem', borderRadius: 5, marginTop: '0.65rem' }}>{r.tag}</div>
                </div>
              ))}
            </div>
          </div>

        </div>{/* /wrap */}

        <Footer />
      </div>{/* /layer */}

      {/* ── TRAILER MODAL ── */}
      {trailer && <TrailerModal item={trailer} onClose={() => setTrailer(null)} />}

      {/* ── AI CHAT ── */}
      <AIChatWidget />
    </div>
  );
}
